import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { AppDispatch, RootState } from '../../store';
import { reauthenticate, sessionExpired } from '../../store/authSlice';
import { loadProjects, loadWorkflows } from '../../store/project/projectSlice';
import { fetchDataDefinitions } from '../../store/dataDefinitionsSlice';
import { onUnauthorized } from '../../services/sessionExpiry';
import { showConfirm } from '../common/AppDialog';
import { isEnter } from '../../utils/keyboard';

/**
 * 로그인 만료 창 — 서버가 401 을 돌려주면(services/sessionExpiry) 지금 화면 위에 뜬다.
 *
 * 화면을 떠나지 않는 까닭: 캔버스 편집은 저장 전까지 메모리에만 있다. 로그인 화면으로 넘기면 그것이 사라진다.
 * 그래서 여기서 다시 인증하고(reauthenticate — status 를 건드리지 않는다), 성공하면 목록만 다시 읽는다.
 * 그래도 로그인 화면으로 가고 싶으면 버튼이 있다(무엇을 잃는지 먼저 묻는다).
 *
 * 예전에는 이 창이 없어서, 열어 둔 채 세션이 끊기면 프로젝트·캔버스가 **조용히 비어 보였다**(현장 2026-09-23).
 */
const SessionExpiredDialog: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const expired = useSelector((s: RootState) => s.auth.expired);
  const busy = useSelector((s: RootState) => s.auth.reauthBusy);
  const error = useSelector((s: RootState) => s.auth.error);
  const lastLoginId = useSelector((s: RootState) => s.auth.user?.loginId || '');
  const projectId = useSelector((s: RootState) => s.project.selectedProjectId);
  const [pwd, setPwd] = useState('');
  const [loginId, setLoginId] = useState(lastLoginId);

  // 401 을 만나면 알려 달라고 등록해 둔다 (앱에 하나만 마운트된다)
  useEffect(() => {
    onUnauthorized(() => dispatch(sessionExpired()));
    return () => onUnauthorized(null);
  }, [dispatch]);

  useEffect(() => { if (expired) { setLoginId(lastLoginId); setPwd(''); } }, [expired, lastLoginId]);

  if (!expired) return null;

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!loginId.trim() || !pwd || busy) return;
    const r = await dispatch(reauthenticate({ loginId: loginId.trim(), pwd }));
    if (reauthenticate.fulfilled.match(r)) {
      // 끊긴 동안 비어 온 목록을 다시 읽는다 — 캔버스(편집 중인 것)는 그대로 둔다
      dispatch(loadProjects());
      if (projectId) dispatch(loadWorkflows(projectId));
      dispatch(fetchDataDefinitions());
    }
  };

  const toLoginScreen = async () => {
    if (!(await showConfirm(t('layout:auth.expired.leaveWarn'), { title: t('layout:auth.expired.title'), tone: 'warning' }))) return;
    window.location.reload();     // 세션이 없으므로 앱 진입 게이트(AppInitializer)가 로그인 화면을 띄운다
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-[380px] max-w-[95vw] p-5 space-y-3">
        <div className="text-base font-semibold text-gray-900">{t('layout:auth.expired.title')}</div>
        <div className="text-xs text-gray-600 leading-relaxed">{t('layout:auth.expired.body')}</div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1" htmlFor="reauth-id">{t('layout:auth.loginId')}</label>
          <input id="reauth-id" autoComplete="username" className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={loginId} onChange={(e) => setLoginId(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1" htmlFor="reauth-pwd">{t('layout:auth.password')}</label>
          <input id="reauth-pwd" type="password" autoFocus autoComplete="current-password"
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={pwd} onChange={(e) => setPwd(e.target.value)}
            onKeyDown={(e) => { if (isEnter(e)) submit(); }} />
        </div>
        {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>}
        <div className="flex items-center justify-between gap-2 pt-1">
          <button type="button" className="text-xs text-gray-500 hover:text-gray-800 underline" onClick={toLoginScreen}>
            {t('layout:auth.expired.toLoginScreen')}
          </button>
          <button type="submit" disabled={busy || !loginId.trim() || !pwd}
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? t('layout:auth.checking') : t('layout:auth.expired.signIn')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SessionExpiredDialog;
